import { computed, inject, provide, ref, type InjectionKey, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { getMascotStateForPsi, MASCOT_ART } from '../data/mascot';
import { useAirQuality } from './useAirQuality';
import { useSharing } from './useSharing';
import { usePuns } from './usePuns';

const bulletinKey: InjectionKey<ReturnType<typeof createAirBulletin>> = Symbol('air-bulletin');

/** Shared state survives navigation; each composable owns its own lifecycle. */
function createAirBulletin(previousDesign: Readonly<Ref<boolean>>) {
  const route = useRoute();
  const air = useAirQuality();
  const shareModalOpen = ref(false);
  const puns = usePuns(air.currentTier, shareModalOpen);
  const sharing = useSharing({
    previousDesign,
    liveData: air.liveData,
    feedStatus: air.feedStatus,
    currentPun: puns.currentPun,
    shareModalOpen,
  });
  const isEmbed = computed(() => route.query.embed !== undefined);
  const mascotState = computed(() => getMascotStateForPsi(air.currentPsi.value));
  const mascotArt = computed(() => MASCOT_ART[mascotState.value]);
  const punBadge = computed(() =>
    puns.currentPun.value.classic
      ? 'A 2015 classic'
      : puns.currentPun.value.custom
        ? 'Community · ' + (puns.currentPun.value.author ?? 'Anonymous')
        : 'HazePun original',
  );
  return { ...air, ...puns, ...sharing, previousDesign, isEmbed, mascotState, mascotArt, punBadge };
}
export function provideAirBulletin(previousDesign: Readonly<Ref<boolean>>) {
  const bulletin = createAirBulletin(previousDesign);
  provide(bulletinKey, bulletin);
  return bulletin;
}
export function useAirBulletin() {
  const bulletin = inject(bulletinKey);
  if (!bulletin) throw new Error('The air bulletin must be provided by App.vue.');
  return bulletin;
}
